import type { SubscriptionTier } from '@personal-trainer-app/shared';

/**
 * The in-app upgrade surface. Every paywall CTA lands here so the upgrade
 * happens inside the app (and can round-trip back to the originating surface),
 * regardless of what the backend put in `meta.subscription.upgrade_url`.
 */
const UPGRADE_BASE_PATH = '/settings';
const UPGRADE_SECTION = 'subscription';

/**
 * Is `url` safe to navigate to *inside* the app?
 *
 * Only a relative, same-origin path counts. A scheme (`https:`, `mailto:`…) or
 * a protocol-relative `//host` is external — the backend `upgrade_url` may be a
 * fully-qualified marketing/billing link, and we deliberately do **not** follow
 * it; we coalesce to the internal route instead (honor-but-coalesce).
 */
function isInternalPath(url: string): boolean {
  if (!url.startsWith('/')) return false; // must be an absolute path
  if (url.startsWith('//')) return false; // protocol-relative → external
  return true;
}

/** The current in-app location, used as the `returnTo` when none is supplied. */
function currentOrigin(): string {
  if (typeof window === 'undefined') return UPGRADE_BASE_PATH;
  return `${window.location.pathname}${window.location.search}`;
}

export interface ResolvePaywallDestinationOptions {
  /**
   * Where the user should be sent back to after upgrading. Defaults to the
   * current location (`pathname + search`). Pass explicitly from surfaces that
   * know their origin (or in tests) to avoid depending on `window.location`.
   */
  origin?: string;
}

/**
 * The two fields the resolver actually reads. A `PaymentRequiredError`
 * satisfies this structurally; so does a plain upgrade intent — e.g. a
 * dashboard module's locked marker (`{ requiredTier: locked.target_plan }`) —
 * so every upgrade CTA shares one coalescing rule regardless of whether a 402
 * was ever thrown.
 */
export interface PaywallDestinationSource {
  requiredTier?: SubscriptionTier | undefined;
  upgradeUrl?: string | undefined;
}

/**
 * Resolve the internal route a paywall CTA should navigate to.
 *
 * Honor-but-coalesce (see the paywall PRD, Phase 2 Task 2.5):
 * - Base: `/settings?section=subscription`.
 * - Carry the upgrade *intent* — `plan=<requiredTier>` (from
 *   `upgrade_prompt.target_plan`) — so the subscription panel can pre-select it.
 * - Carry `returnTo=<origin>` so {@link SubscriptionPanel} can navigate the user
 *   back to where they hit the wall after a successful upgrade (Phase 5).
 * - An **internal/relative** `upgradeUrl` is honored as the base path; an
 *   **external** one (absolute URL / protocol-relative) is coalesced to the
 *   internal route so the upgrade stays in-app.
 *
 * Always returns a relative path suitable for react-router `navigate`.
 */
export function resolvePaywallDestination(
  error: PaywallDestinationSource,
  { origin }: ResolvePaywallDestinationOptions = {}
): string {
  const returnTo = origin ?? currentOrigin();

  // Honored internal upgradeUrl, otherwise the canonical subscription route.
  const base =
    error.upgradeUrl && isInternalPath(error.upgradeUrl)
      ? error.upgradeUrl
      : `${UPGRADE_BASE_PATH}?section=${UPGRADE_SECTION}`;

  const [path, query = ''] = base.split('?');
  const params = new URLSearchParams(query);

  // Preserve any params the honored internal URL already carried; only fill in
  // the plan hint when it is absent so the backend's own link wins.
  if (error.requiredTier && !params.has('plan')) {
    params.set('plan', error.requiredTier);
  }
  // The live origin is authoritative for the round trip — overwrite any stale
  // returnTo the backend link may have carried.
  params.set('returnTo', returnTo);

  return `${path}?${params.toString()}`;
}
